<script setup>
import { computed, inject } from 'vue';
import { timelineItems } from '../timeline.js';
import { styleVars } from './shared.js';

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

// A timeline with no milestones is not drawn on the public page at
// all, heading included (Mus, 2026-10-11). A milestone without a photo
// is ordinary, so the public page draws none. In the editor the empty slot shows, so a manager can see where
// one would go.
const editing = inject('tiqEditorCanvas', false);
const items = computed(() => timelineItems(props.config));
</script>

<template>
    <section v-if="items.length || editing" :id="config.anchor_id || null" class="th-block th-sec th-tl" :style="styleVars(config)">
        <div class="th-wrap">
            <div v-if="config.eyebrow || config.heading" class="th-head th-tl__head">
                <div>
                    <p v-if="config.eyebrow" class="th-eyebrow">{{ config.eyebrow }}</p>
                    <h2 v-if="config.heading" class="th-h2">{{ config.heading }}</h2>
                </div>
            </div>

            <ol v-if="items.length" class="th-tl__list">
                <li v-for="(t, i) in items" :key="i" class="th-tl__item">
                    <div class="th-tl__rail" aria-hidden="true">
                        <span class="th-tl__dot"></span>
                        <span class="th-tl__line"></span>
                    </div>
                    <div class="th-tl__body">
                        <div v-if="t.image || editing" class="th-media th-tl__media">
                            <img v-if="t.image" :src="t.image" :alt="t.alt" loading="lazy">
                            <span v-else class="th-media__label">Add a photo</span>
                        </div>
                        <div class="th-tl__text">
                            <p v-if="t.date" class="th-tl__date">{{ t.date }}</p>
                            <h3 v-if="t.title" class="th-tl__title">{{ t.title }}</h3>
                            <p v-if="t.text" class="th-tl__desc">{{ t.text }}</p>
                        </div>
                    </div>
                </li>
            </ol>
            <p v-else-if="editing" class="th-small th-tl__empty">Add milestones in the editor.</p>
        </div>
    </section>
</template>

<style scoped>
.th-tl__head { margin-bottom: clamp(28px, 4vw, 48px); }
/* The list sizes itself against its own width rather than the
   screen's, so the same block reads as the compact version inside a
   column and on a phone. */
.th-tl__list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; container-type: inline-size; }
.th-tl__item { display: grid; grid-template-columns: 16px minmax(0, 1fr); gap: clamp(16px, 3vw, 32px); }
.th-tl__rail { display: flex; flex-direction: column; align-items: center; }
.th-tl__dot {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: var(--accent);
    box-shadow: 0 0 0 5px var(--block-bg, var(--th-paper)), 0 0 0 6px var(--th-rule);
    flex: none;
    margin-top: 4px;
}
.th-tl__line { flex: 1; width: 2px; background: var(--th-rule); margin-top: 8px; }
.th-tl__body {
    display: flex;
    flex-wrap: wrap;
    gap: clamp(16px, 3vw, 32px);
    padding-bottom: clamp(32px, 5vw, 56px);
}
.th-tl__item:last-child .th-tl__body { padding-bottom: 8px; }
.th-tl__media { flex: 1 1 240px; max-width: 380px; aspect-ratio: 4 / 3; }
.th-tl__text { flex: 1 1 300px; min-width: 0; display: flex; flex-direction: column; gap: 10px; }
.th-tl__date { margin: 0; font: 500 13px/1 var(--th-mono); letter-spacing: .08em; text-transform: uppercase; color: var(--text-eyebrow, var(--accent)); }
.th-tl__title {
    margin: 0;
    font: 800 clamp(22px, 2.6vw, 30px)/1.1 var(--th-display);
    font-stretch: 106%;
    text-wrap: balance;
    color: var(--text-heading, inherit);
}
.th-tl__desc { margin: 0; font-size: 17px; color: var(--text-sub, var(--th-muted)); max-width: 60ch; text-wrap: pretty; }
.th-tl__empty { font-style: italic; }

@container (max-width: 520px) {
    .th-tl__item { grid-template-columns: 12px minmax(0, 1fr); gap: 14px; }
    .th-tl__dot { width: 12px; height: 12px; box-shadow: none; margin-top: 3px; }
    .th-tl__line { margin-top: 6px; }
    .th-tl__body { flex-direction: column; flex-wrap: nowrap; gap: 8px; padding-bottom: 24px; }
    .th-tl__media { flex: none; max-width: none; width: 100%; aspect-ratio: 16 / 9; }
    .th-tl__text { flex: none; gap: 8px; }
    .th-tl__date { font-size: 11px; letter-spacing: .1em; }
    .th-tl__title { font-size: 18px; line-height: 1.2; font-stretch: 100%; }
    .th-tl__desc { font-size: 15px; }
}
</style>
